import { useState, useCallback } from 'react';
import callSettingsService from '../services/callSettingsService';

/**
 * useManualCalling Hook
 * Encapsulates all manual calling logic for reuse across components
 * 
 * Returns:
 * - callSettings: object - Current call settings
 * - isLoadingCallSettings: boolean - Loading state for call settings
 * - phoneSelectionModalVisible: boolean - Phone selection modal visibility
 * - selectedCountryIndex: number - Selected country index
 * - selectedPhoneIndex: number - Selected phone index
 * - isCalling: boolean - Whether a call is active
 * - activeCallUUID: string - UUID of active call
 * - fetchCallSettings: function - Fetch call settings from backend
 * - openPhoneSelectionModal: function - Open phone selection modal
 * - closePhoneSelectionModal: function - Close phone selection modal
 * - setSelectedCountryIndex: function - Update selected country
 * - setSelectedPhoneIndex: function - Update selected phone
 * - setIsCalling: function - Update calling state
 * - setActiveCallUUID: function - Update active call UUID
 * - initiateCall: function - Make a call with selected phone
 * - endCall: function - End the active call
 * - toggleMute: function - Toggle mute on active call
 * - toggleSpeaker: function - Toggle speaker on active call
 */
const useManualCalling = (messageApi) => {
  const [callSettings, setCallSettings] = useState(null);
  const [isLoadingCallSettings, setIsLoadingCallSettings] = useState(false);
  const [phoneSelectionModalVisible, setPhoneSelectionModalVisible] = useState(false);
  const [selectedCountryIndex, setSelectedCountryIndex] = useState(0);
  const [selectedPhoneIndex, setSelectedPhoneIndex] = useState(0);
  const [isCalling, setIsCalling] = useState(false);
  const [activeCallUUID, setActiveCallUUID] = useState(null);

  // Fetch call settings from backend
  const fetchCallSettings = useCallback(async () => {
    try {
      console.log('[fetchCallSettings] Starting to fetch call settings');
      setIsLoadingCallSettings(true);
      const response = await callSettingsService.getCallSettings();
      console.log('[fetchCallSettings] Response:', response);
      if (response.success && response.data) {
        console.log('[fetchCallSettings] Setting call settings:', response.data);
        setCallSettings(response.data);
      } else {
        console.log('[fetchCallSettings] Response not successful or no data');
      }
    } catch (error) {
      console.error('[fetchCallSettings] Error fetching call settings:', error);
    } finally {
      setIsLoadingCallSettings(false);
    }
  }, []);

  // Open phone selection modal
  const openPhoneSelectionModal = useCallback(() => {
    if (!callSettings || !callSettings.plivoSettings || callSettings.plivoSettings.countries.length === 0) {
      messageApi.warning("No phone numbers configured in call settings. Please configure call settings first.");
      return;
    }
    setPhoneSelectionModalVisible(true);
  }, [callSettings, messageApi]);

  // Close phone selection modal
  const closePhoneSelectionModal = useCallback(() => {
    setPhoneSelectionModalVisible(false);
  }, []);

  // Stop ringing audio if present
  const stopRingingAudio = useCallback(() => {
    const ringingAudio = document.getElementById('plivo-ringing-audio');
    if (ringingAudio) {
      ringingAudio.pause();
      ringingAudio.currentTime = 0;
    }
  }, []);

  // Initiate a call with selected phone
  const initiateCall = useCallback(
    async (phoneNumber) => {
      if (!phoneNumber) {
        messageApi.error("No phone number available to call.");
        return;
      }

      if (!window._plivo?.client) {
        messageApi.error("Plivo client not initialized.");
        console.error("Plivo client not initialized.");
        return;
      }

      try {
        setIsCalling(true);
        messageApi.open({ type: "loading", content: "Calling...", duration: 0 });

        // Get selected country and phone
        const selectedCountry = callSettings.plivoSettings.countries[selectedCountryIndex];
        const selectedPhone = selectedCountry.phoneNumbers[selectedPhoneIndex];

        // Normalize destination number: strip all non-digits and format as +<digits>
        const digitsOnly = String(phoneNumber).replace(/\D/g, '');
        if (!digitsOnly) {
          messageApi.error("Invalid phone number. Please check the number and try again.");
          setIsCalling(false);
          messageApi.destroy();
          return;
        }
        const formattedNumber = `+${digitsOnly}`;

        console.log(`[Call] Making call from ${selectedPhone} (${selectedCountry.countryName}) to ${formattedNumber}`);

        // Make call
        const callUUID = window._plivo.client.call(formattedNumber);
        setActiveCallUUID(callUUID);

        // Close modal
        setPhoneSelectionModalVisible(false);
      } catch (error) {
        console.error("Error during browser call:", error);
        messageApi.error("Failed to initiate browser call.");
        messageApi.destroy();
        setIsCalling(false);
      }
    },
    [callSettings, selectedCountryIndex, selectedPhoneIndex, messageApi]
  );

  // End the active call
  const endCall = useCallback(() => {
    stopRingingAudio();
    if (window._plivo?.client && activeCallUUID) {
      try {
        window._plivo.client.hangup();
        setActiveCallUUID(null);
        setIsCalling(false);
        messageApi.destroy();
        messageApi.info("Call ended");
      } catch (error) {
        console.error("Error ending call:", error);
        messageApi.error("Failed to end call");
      }
    }
  }, [activeCallUUID, messageApi, stopRingingAudio]);

  // Toggle mute on active call
  const toggleMute = useCallback(() => {
    try {
      if (!window._plivo?.client) {
        messageApi.error("Plivo client not initialized.");
        return;
      }

      if (!activeCallUUID) {
        messageApi.warning("No active call.");
        return;
      }

      const isMuted = window._plivo.client.isMuted(activeCallUUID);

      if (isMuted) {
        window._plivo.client.unmute(activeCallUUID);
        console.log('[Call] Unmuted');
        messageApi.info('Unmuted');
      } else {
        window._plivo.client.mute(activeCallUUID);
        console.log('[Call] Muted');
        messageApi.info('Muted');
      }
    } catch (error) {
      console.error('[Call] Error toggling mute:', error);
      messageApi.error('Failed to toggle mute');
    }
  }, [activeCallUUID, messageApi]);

  // Toggle speaker on active call
  const toggleSpeaker = useCallback(() => {
    try {
      if (!window._plivo?.client) {
        messageApi.error("Plivo client not initialized.");
        return;
      }

      if (!activeCallUUID) {
        messageApi.warning("No active call.");
        return;
      }

      console.log('[Call] Speaker toggle requested');
      messageApi.info('Speaker toggled');
    } catch (error) {
      console.error('[Call] Error toggling speaker:', error);
      messageApi.error('Failed to toggle speaker');
    }
  }, [activeCallUUID, messageApi]);

  return {
    callSettings,
    isLoadingCallSettings,
    phoneSelectionModalVisible,
    selectedCountryIndex,
    selectedPhoneIndex,
    isCalling,
    activeCallUUID,
    fetchCallSettings,
    openPhoneSelectionModal,
    closePhoneSelectionModal,
    setSelectedCountryIndex,
    setSelectedPhoneIndex,
    setIsCalling,
    setActiveCallUUID,
    initiateCall,
    endCall,
    toggleMute,
    toggleSpeaker,
  };
};

export default useManualCalling;
